import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Radio, Clock, ListChecks, ChevronRight, Loader2, Search } from "lucide-react";
import Pill from "@/components/ui-v2/Pill";
import MeetingSummaryDialog from "@/components/MeetingSummaryDialog";

function fmtDuration(s) {
  const n = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${n}s`;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " · " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * MeetingHistory — a reusable list of recorded meetings. Fetches
 * GET /meetings/recordings with `params` (scope | chat_id | project_folder_id).
 * Clicking a row opens the existing MeetingSummaryDialog (summary + transcript).
 */
function escapeRe(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

function Highlight({ text, term }) {
  if (!term || !text) return text || null;
  const parts = String(text).split(new RegExp(`(${escapeRe(term)})`, "ig"));
  return parts.map((p, i) =>
    p.toLowerCase() === term.toLowerCase()
      ? <mark key={i} className="bg-brand/30 text-ink rounded px-0.5">{p}</mark>
      : <span key={i}>{p}</span>,
  );
}

export default function MeetingHistory({ params = {}, emptyText = "No recorded meetings yet.", showChatName = true }) {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(() => {
    setRows(null);
    const reqParams = debounced ? { ...params, q: debounced } : params;
    api.get("/meetings/recordings", { params: reqParams })
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), debounced]);

  useEffect(() => { load(); }, [load]);

  const searchBox = (
    <div className="flex items-center gap-2 bg-surface2 border border-hairline rounded-xl px-3 py-2 mb-3 mx-1" data-testid="meeting-search">
      <Search className="w-4 h-4 text-ink-mute shrink-0" />
      <input
        data-testid="meeting-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search meetings — titles, summaries & transcripts"
        className="flex-1 bg-transparent text-[14px] text-ink placeholder:text-ink-mute outline-none"
      />
      {query && (
        <button data-testid="meeting-search-clear" onClick={() => setQuery("")} className="text-ink-mute hover:text-ink text-[12px]">Clear</button>
      )}
    </div>
  );

  if (rows === null) {
    return (
      <div data-testid="meeting-history-searching">
        {searchBox}
        <div className="flex items-center justify-center py-10 text-ink-mute" data-testid="meeting-history-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div>
        {searchBox}
        <div className="py-10 text-center" data-testid="meeting-history-empty">
          <Radio className="w-10 h-10 text-ink-mute mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-[13px] text-ink-dim">{debounced ? `No meetings match “${debounced}”.` : emptyText}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="meeting-history">
      {searchBox}
      {rows.map((r) => {
        const actionCount = (r.action_items || []).length || r.action_item_count || 0;
        return (
          <button
            key={r.id}
            type="button"
            data-testid={`meeting-history-row-${r.id}`}
            onClick={() => setOpenId(r.id)}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.03] active:bg-white/[0.05] transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
              <Radio className="w-4 h-4 text-brand" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-semibold text-ink truncate">{r.title || "Meeting"}</span>
                {showChatName && r.destination_chat_name && (
                  <Pill tone={r.destination_chat_kind === "project" ? "brand" : "default"} size="sm">
                    {r.destination_chat_name}
                  </Pill>
                )}
              </div>
              <div className="flex items-center gap-3 mt-0.5 text-[11px] text-ink-mute">
                <span>{fmtDate(r.started_at)}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDuration(r.active_duration_seconds)}</span>
                {actionCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-brand"><ListChecks className="w-3 h-3" /> {actionCount}</span>
                )}
                {r.status && r.status !== "complete" && (
                  <span className="text-amber-300 capitalize">{r.status}</span>
                )}
              </div>
              {r.snippet && (
                <p className="text-[11px] text-ink-dim mt-1 line-clamp-2" data-testid={`meeting-history-snippet-${r.id}`}>
                  <Highlight text={r.snippet} term={debounced} />
                </p>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-ink-mute shrink-0" />
          </button>
        );
      })}

      {openId && (
        <MeetingSummaryDialog
          open={!!openId}
          onOpenChange={(v) => { if (!v) setOpenId(null); }}
          callId={openId}
        />
      )}
    </div>
  );
}
